import { devLog, enableDevLoggingFromSettings, getPopup } from "./common";
import HttpClient from "./http-client";
import { PopupOptions } from "./popup";
import ProductCount from "./product-count";
import { logProductEvent, ProductEventType } from "./product-event";
import { TronLogoEvent } from "./tron-logo-event";

const resourceTypeTronLogoLogo = 11204;

export class CalculatorOptions {
    parentId: number;
    tronLogoFileGuid: string;
    tronLogoFileType: number;
    productCounts: ProductCount[];
    modelGuid: string;
    positionGuid: string;
    technologyGuid: string;
    calculationGuid: string;
    supplierGuid: string;
    dimension1: string;
    dimension2: string;
}

interface CalculatorLaunchResult {
    isActive: boolean;
    url: string;
    tronDesignerEnabled: boolean;
    isDevelopment?: boolean;
}

export default class TronCalculator {

    async openExistingCalculation(calculationGuid: string): Promise<void> {
        await this.openCalculator({ calculationGuid: calculationGuid } as CalculatorOptions);
    }

    async openFromMatrix(parentId: number, productModelGuid: string, supplierGuid: string, productCounts: ProductCount[], technologyGuid: string = null, positionGuid: string = null) {
        await this.openCalculator({
            parentId: parentId,
            modelGuid: productModelGuid,
            supplierGuid: supplierGuid,
            productCounts: productCounts,
            positionGuid: positionGuid,
            technologyGuid: technologyGuid
        } as CalculatorOptions);
    }

    async openForProduct(parentId: number, productModelGuid: string, supplierGuid: string, dimension1: string, dimension2: string): Promise<void> {
        await this.openCalculator({
            parentId: parentId,
            modelGuid: productModelGuid,
            supplierGuid: supplierGuid,
            dimension1: dimension1,
            dimension2: dimension2
        } as CalculatorOptions);
    }

    private async openCalculator(options: CalculatorOptions): Promise<void> {
        if (!options.tronLogoFileGuid && options.parentId) {
            const tronLogoEvent = await this.loadTronLogoFileGuid(options.parentId);
            options.tronLogoFileGuid = tronLogoEvent?.motiveId ?? tronLogoEvent?.visualId;
            options.tronLogoFileType = tronLogoEvent?.resourceType;
        }

        let launch: CalculatorLaunchResult;
        try {
            launch = await this.loadLaunchUrl(options);
        } catch (error) {
            // HttpClient has already shown the localized error toast; keep the rejection from
            // bubbling out of the click handler and leave a trace for field debugging.
            console.warn("TronCalculator launch request failed", error);
            return;
        }

        enableDevLoggingFromSettings(launch.isDevelopment === true);
        devLog("TronCalculator launch", {
            isActive: launch.isActive,
            tronDesignerEnabled: launch.tronDesignerEnabled,
            urlLength: launch.url?.length ?? 0,
        });

        if (!launch.isActive || !launch.url) {
            console.warn("TronCalculator launch refused by the server", { isActive: launch.isActive });
            return;
        }

        this.openIFrame(launch.url, options);
    }

    // The whole session — shop, customer, language, currency, tracking, return url and the customer's
    // discount rates — is resolved and signed on the server; nothing is appended to the url here.
    private async loadLaunchUrl(options: CalculatorOptions): Promise<CalculatorLaunchResult> {
        const response = await new HttpClient().fetch("/api/calculation/launch", {
            method: "POST",
            cache: "no-store",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                productId: options.parentId,
                calculationGuid: options.calculationGuid,
                modelGuid: options.modelGuid,
                supplierGuid: options.supplierGuid,
                positionGuid: options.positionGuid,
                technologyGuid: options.technologyGuid,
                dimension1: options.dimension1,
                dimension2: options.dimension2,
                tronLogoFileGuid: options.tronLogoFileGuid,
                tronLogoFileType: options.tronLogoFileType,
                productCounts: options.productCounts?.map(c => ({
                    productGuid: c.productGuid,
                    quantity: c.quantity
                }))
            })
        });

        return await response.json() as CalculatorLaunchResult;
    }

    private openIFrame(url: string, options: CalculatorOptions): void {
        getPopup({
            id: 'tron-calculator-popup',
            onClose: () => {
                void logProductEvent({
                    type: ProductEventType.CloseCalculator,
                    productModelGuid: options.modelGuid,
                    calculationGuid: options.calculationGuid,
                });
            }
        } as PopupOptions).open(`<iframe src="${url}" scrolling="auto" style="overflow:hidden;overflow-x:hidden;overflow-y:hidden;height:100%;width:100%;position:absolute;top:0px;left:0px;right:0px;bottom:0px" height="100%" width="100%" frameborder="0"></iframe>`);
    }

    private async loadTronLogoFileGuid(parentId: number): Promise<TronLogoEvent> {
        const url = `/api/log/last-motive?productId=${parentId}`;
        const response = await new HttpClient().fetch(url, null, (e) => { });
        // 204 = no previous motive for this visitor/product (the endpoint's "nothing found" answer). It has
        // an empty body, so .json() would throw — only parse a real 200.
        const event = response.ok && response.status !== 204 ? (await response.json() as TronLogoEvent) : null;
        if (event) return event;
        const currentLogo = window.tron?.User?.getUserLogo() ?? null;
        return currentLogo ? { motiveId: currentLogo, resourceType: resourceTypeTronLogoLogo } as TronLogoEvent : null;
    }
}
